// SPDX-License-Identifier: BUSL-1.1

/**
 * Flag health summary card — aggregate flag status for the dashboard.
 *
 * Shows total count, enabled/disabled breakdown, and provider distribution.
 *
 */

import type * as React from 'react';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { Flag } from '@/types/api';
import { PROVIDER_COLORS } from '@/types/api';

interface FlagHealthCardProps {
  flags: Flag[] | undefined;
  isLoading: boolean;
}

/** Count occurrences of each unique value for a given field. */
function countBy(flags: Flag[], key: keyof Flag): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const flag of flags) {
    const value = String(flag[key] ?? 'unknown');
    counts[value] = (counts[value] ?? 0) + 1;
  }
  return counts;
}

export function FlagHealthCard({ flags, isLoading }: FlagHealthCardProps): React.ReactElement {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Flag Health</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  const data = flags ?? [];
  const total = data.length;
  const enabled = data.filter((f) => f.enabled).length;
  const disabled = total - enabled;
  const providers = countBy(data, 'provider');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Flag Health</CardTitle>
        <Link
          to="/flags"
          className="text-xs text-neutral-500 transition hover:text-neutral-300"
        >
          Manage →
        </Link>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Total */}
        <div>
          <p className="font-mono text-2xl font-semibold text-neutral-100">{total}</p>
          <p className="text-xs text-neutral-500">total flags</p>
        </div>

        {/* Enabled / Disabled breakdown */}
        <div className="flex gap-4">
          <div className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-green-400" />
            <span className="font-mono text-sm text-green-400">{enabled}</span>
            <span className="text-xs text-neutral-500">enabled</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-neutral-500" />
            <span className="font-mono text-sm text-neutral-400">{disabled}</span>
            <span className="text-xs text-neutral-500">disabled</span>
          </div>
        </div>

        {/* Provider distribution */}
        {total > 0 && (
          <div className="space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
              By Provider
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(providers)
                .sort(([, a], [, b]) => b - a)
                .map(([provider, count]) => (
                  <span
                    key={provider}
                    className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs ${PROVIDER_COLORS[provider] ?? 'bg-neutral-800 text-neutral-400'}`}
                  >
                    {provider}
                    <span className="font-mono">{count}</span>
                  </span>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
